'use client';

import { useTranslations } from 'next-intl';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface PaginationControlsProps {
  page: number;
  pageCount: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  pageSizeOptions?: number[];
  className?: string;
}

export function PaginationControls({
  page,
  pageCount,
  perPage,
  total,
  onPageChange,
  onPerPageChange,
  pageSizeOptions = [5, 10, 20, 30, 40, 50, 100],
  className
}: PaginationControlsProps) {
  const t = useTranslations('common');

  const canPrev = page > 1;
  const canNext = page < pageCount;

  return (
    <div
      dir='ltr'
      className={cn(
        'flex w-full flex-col-reverse items-center justify-between gap-4 overflow-auto p-1 sm:flex-row sm:gap-8',
        className
      )}
    >
      <div className='text-muted-foreground flex-1 text-sm whitespace-nowrap'>
        {t('rowsTotal', { count: total })}
      </div>
      <div className='flex flex-col-reverse items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8'>
        <div className='flex items-center space-x-2'>
          <p className='text-sm font-medium whitespace-nowrap'>
            {t('rowsPerPage')}
          </p>
          <Select
            value={`${perPage}`}
            onValueChange={(value) => onPerPageChange(Number(value))}
          >
            <SelectTrigger className='h-8 w-[4.5rem] [&[data-size]]:h-8'>
              <SelectValue placeholder={perPage} />
            </SelectTrigger>
            <SelectContent side='top'>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={`${size}`}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className='flex items-center justify-center text-sm font-medium'>
          {t('pageXofY', { page, total: pageCount })}
        </div>
        <div className='flex items-center space-x-2'>
          <Button
            aria-label={t('goToFirstPage')}
            variant='outline'
            size='icon'
            className='hidden size-8 lg:flex'
            onClick={() => onPageChange(1)}
            disabled={!canPrev}
          >
            <ChevronsLeft />
          </Button>
          <Button
            aria-label={t('goToPrevPage')}
            variant='outline'
            size='icon'
            className='size-8'
            onClick={() => onPageChange(page - 1)}
            disabled={!canPrev}
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            aria-label={t('goToNextPage')}
            variant='outline'
            size='icon'
            className='size-8'
            onClick={() => onPageChange(page + 1)}
            disabled={!canNext}
          >
            <ChevronRightIcon />
          </Button>
          <Button
            aria-label={t('goToLastPage')}
            variant='outline'
            size='icon'
            className='hidden size-8 lg:flex'
            onClick={() => onPageChange(pageCount)}
            disabled={!canNext}
          >
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
